import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Coffee } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuthStore } from '../../../store/authStore';
import { getSquareStatus, type SquareStatus } from '../../../services/square-service';
import { useShopSettingsLoad } from './useShopSettingsLoad';
import OwnerShell, { OwnerLoadingShell } from '../OwnerShell';
import CoralHeader from '../../dashboard/CoralHeader';
import BillingCard from '../BillingCard';
import AnalyticsPreferences from '../AnalyticsPreferences';

interface SettingsCard {
    icon: string;
    name: string;
    hint: string;
    path: string;
}

/**
 * SETUP tab landing (Task 14 of the ShopSettings split; restyled + expanded
 * by Task 15 per mockup #8b). Two parts:
 *  1. Quick-glance/action cards drawn straight from #8b — Counter QR preview
 *     (taps through to the fuller ShopQRCode card on /shops/settings/counter),
 *     a read-only Square POS status card (the connect action itself stays on
 *     the COUNTER tab), the real BillingCard, and AnalyticsPreferences'
 *     `compact` toggle row.
 *  2. The four settings-group nav cards (STOREFRONT / REWARDS / COUNTER /
 *     ACCOUNT) that route to their own sub-screens (Tasks 10-13). Hints are
 *     derived from the public shop row the shared loader already fetches —
 *     no extra network calls for this half of the page.
 */
function SettingsHub() {
    const navigate = useNavigate();
    const { userAddress } = useAuthStore();
    const { loading, shopId, shopName, shopRow } = useShopSettingsLoad();

    // Square status for the read-only status card below — the CONNECT action
    // itself stays on the COUNTER tab (ShopOwnerDashboard, restyled by this
    // same task); this is a second, non-interactive read of the same
    // existing getSquareStatus() call ShopOwnerDashboard already makes.
    const [squareStatus, setSquareStatus] = useState<SquareStatus | null>(null);
    useEffect(() => {
        if (!userAddress) return;
        getSquareStatus().then(setSquareStatus).catch(() => setSquareStatus(null));
    }, [userAddress]);

    if (loading) {
        return (
            <OwnerLoadingShell active="setup" statusLabel="SETUP" title="Loading…">
                <div className="space-y-3.5">
                    <div className="bg-white rounded-[10px] h-24 animate-pulse" />
                    <div className="bg-white rounded-[10px] h-64 animate-pulse" />
                </div>
            </OwnerLoadingShell>
        );
    }

    if (!shopId) {
        return (
            <OwnerLoadingShell active="setup" statusLabel="SETUP" title="No shop yet">
                <div className="bg-white rounded-[10px] text-center py-12">
                    <Coffee size={40} className="mx-auto mb-4 text-coral" />
                    <p className="font-heading text-[10px] text-body-grey">NO SHOP REGISTERED YET</p>
                </div>
            </OwnerLoadingShell>
        );
    }

    const beanCount = Array.isArray(shopRow?.beans) ? shopRow!.beans!.length : 0;
    const specialCount = Array.isArray(shopRow?.specials) ? shopRow!.specials!.length : 0;
    const tierCount = Array.isArray(shopRow?.reward_tiers) ? shopRow!.reward_tiers!.length : 0;

    const cards: SettingsCard[] = [
        {
            icon: '🏪',
            name: 'Storefront',
            hint: `LOGO ${shopRow?.image_url ? 'SET' : 'MISSING'} · ${beanCount} BEANS · ${specialCount} SPECIALS`,
            path: '/shops/settings/storefront',
        },
        {
            icon: '🎁',
            name: 'Rewards',
            hint: `${tierCount} TIERS`,
            path: '/shops/settings/rewards',
        },
        {
            icon: '🧾',
            name: 'Counter',
            hint: 'QR READY',
            path: '/shops/settings/counter',
        },
        {
            icon: '📡',
            name: 'NFC tag',
            hint: 'TAP-TO-STAMP · FREE',
            path: '/shops/settings/tag',
        },
        {
            icon: '⚙️',
            name: 'Account',
            hint: 'ANALYTICS & BILLING',
            path: '/shops/settings/account',
        },
    ];

    // Same URL-building logic ShopQRCode.tsx uses — duplicated here for a
    // compact preview tile rather than embedding the full ShopQRCode card
    // (which already lives, unabridged, on the COUNTER settings sub-page).
    const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    const ownerAddress = shopRow?.owner_address || userAddress || '';
    const shopLink = ownerAddress ? `${appUrl}/?shop=${ownerAddress}` : '';

    return (
        <OwnerShell
            active="setup"
            header={<CoralHeader statusLabel="SETUP" eyebrow="OWNER SETTINGS" title={shopName} />}
        >
        <div className="space-y-3.5 pb-8">
            {/* sr-only: real landmark heading; CoralHeader's title already
                shows the shop name visually. */}
            <h1 className="sr-only">Setup</h1>

            {/* Counter QR — mockup #8b's compact 64px tile. Tap through to the
                COUNTER settings sub-page for the full QR card (copy link/print). */}
            {shopLink && (
                <button
                    onClick={() => navigate('/shops/settings/counter')}
                    className="w-full bg-white rounded-[10px] p-[18px] flex items-center justify-between gap-3.5 text-left"
                >
                    <div className="min-w-0">
                        <p className="font-grotesk font-bold text-[17px] text-primary mb-[5px]">Counter QR</p>
                        <p className="font-grotesk font-medium text-[13px] leading-[1.4] text-body-grey">Customers scan to join and stamp</p>
                    </div>
                    <div className="flex-none w-16 h-16 rounded-[8px] bg-primary p-2 flex items-center justify-center">
                        <QRCodeSVG value={shopLink} size={48} level="M" bgColor="#111111" fgColor="#ffffff" />
                    </div>
                </button>
            )}

            {/* Square POS — read-only status. The CONNECT action stays on the
                COUNTER tab (ShopOwnerDashboard) — this mirrors that same
                getSquareStatus() read, not a second connect flow. */}
            <div className="bg-white rounded-[10px] p-[18px]">
                <div className="flex items-center justify-between gap-3 mb-2">
                    <p className="font-grotesk font-bold text-[17px] text-primary">Square POS</p>
                    {squareStatus?.connected && (
                        <span className="rounded-full bg-success/[.14] text-[#2f9440] font-heading text-[8px] px-3 py-[7px]">
                            CONNECTED
                        </span>
                    )}
                </div>
                {squareStatus?.connected ? (
                    <>
                        <p className="font-grotesk font-medium text-[13px] leading-[1.5] text-body-grey mb-2.5">
                            Purchases at your till auto-confirm stamps — customers tap in-app to claim.
                        </p>
                        <p className="font-heading text-[8px] text-[#c8c2b4]">
                            MERCHANT {squareStatus.merchant_id?.slice(0, 10)}…
                            {squareStatus.connected_at ? ` · SINCE ${squareStatus.connected_at.slice(0, 10)}` : ''}
                        </p>
                    </>
                ) : (
                    <p className="font-grotesk font-medium text-[13px] leading-[1.5] text-body-grey">
                        Not connected — head to the Counter tab to connect Square and auto-confirm stamps at the till.
                    </p>
                )}
            </div>

            {/* Billing — the real, self-contained BillingCard (prices/founders
                pill/cancel copy are all its own, unchanged). */}
            {shopId && <BillingCard shopId={shopId} />}

            {/* Share anonymised stats — mockup's teal toggle, wired to the
                real (only) existing AnalyticsPreferences boolean state. */}
            <AnalyticsPreferences compact />

            <div className="space-y-2.5 pt-1">
                {cards.map((card) => (
                    <button
                        key={card.path}
                        onClick={() => navigate(card.path)}
                        className="w-full bg-white rounded-[10px] p-[18px] flex items-center gap-3.5 text-left"
                    >
                        <span className="text-2xl shrink-0" aria-hidden>{card.icon}</span>
                        <span className="flex-1 min-w-0">
                            <span className="font-grotesk font-bold text-[15px] text-primary block">{card.name}</span>
                            <span className="block font-grotesk font-medium text-[12px] text-body-grey mt-0.5">{card.hint}</span>
                        </span>
                        <ChevronRight size={16} className="text-body-grey shrink-0" />
                    </button>
                ))}
            </div>
        </div>
        </OwnerShell>
    );
}

export default SettingsHub;
